/**
 * @typedef {object} SiteMap
 * @property {string} id
 * @property {SiteMapNode[]} routes
 * @property {boolean} [hidenav]
 * @property {boolean} [drillIn]
 */

/**
 * @typedef {object} SiteMapNode
 * @property {string} path
 * @property {string} title
 * @property {string} [icon]
 * @property {string} [helpIconMessage]
 * @property {boolean} [hasPageTitleOverride]
 * @property {boolean} [excludeFromMenu]
 * @property {boolean} [isConfigPortal]
 * @property {SiteMapNode[]} [children]
 */

 import messages from "../helpIconMessages";

/** @type {SiteMap[]} */
export default {
  routes: [
    {
      path: "/",
      title: "Home",
      icon: "fas fa-home-lg-alt",
      children: [
        {
          path: "/snapshot",
          title: "Snapshot",
          icon: ""
        },
        {
          path: "/workflow2",
          title: "Tasks",
          icon: ""
        }
      ]
    },
    // Reputation
    {
      path: "/reviews/summary",
      title: "Reputation",
      icon: "fas fa-comment-alt-lines",
      children: [
        {
          path: "/reviews/summary",
          title: "Summary",
          icon: ""
        },
        {
          path: "/reviews",
          title: "Reviews",
          icon: ""
        },
        // TODO- Review Builder Drill-ins
        {
          path: "/reviewBuilder",
          title: "Review Builder",
          hasPageTitleOverride: true,
          icon: ""
        },
        {
          path: "/media",
          title: "Images",
          icon: ""
        }
      ]
    },
    // Social
    {
      path: "/social",
      title: "Social",
      icon: "fas fa-user-friends",
      children: [
        {
          path: "/social/summary",
          title: "Summary",
          icon: "",
          children: [
            {
              path: "/social/summary/metrics",
              title: "Metrics",
              hasPageTitleOverride: true
            },
            {
              path: "/social/summary/topPerformingPosts",
              title: "Top Performing Posts",
              hasPageTitleOverride: true
            },
            {
              path: "/social/summary/providerBreakdown",
              title: "Provider Breakdown",
              hasPageTitleOverride: true
            }
          ]
        },
        {
          path: "/social/activity",
          title: "Feed",
          icon: "",
          children: [
            {
              path: "/social/activity",
              title: "Posts and Comments",
              hasPageTitleOverride: true
            },
            {
              path: "/social/hashtagMetrics",
              title: "Hashtag Metrics",
              hasPageTitleOverride: true
            }
          ]
        },
        {
          path: "/social/publishing",
          title: "Publish",
          icon: ""
        },
        {
          path: "/social/approvals",
          title: "Approvals",
          icon: ""
        }
      ]
    },
    {
      path: "/listings",
      title: "Locations",
      icon: "fas fa-map-marker-alt",
      children: [
        {
          path: "/listings/accuracy",
          title: "Listings Accuracy",
          icon: "",
        },
        {
          path: "/listings/summary",
          title: "Listings Summary",
          helpIconMessage: messages.listingSummary,
          icon: "",
        },
        {
          path: "/listings/listingOptimization",
          title: "Listings Optimization",
          helpIconMessage: messages.listingOptimization,
          icon: "",
        },
        {
          path: "/listings/missingListings",
          title: "Missing Listings",
          icon: "",
        },
        {
          path: "/listings/duplicateListings",
          title: "Duplicate Listings",
          helpIconMessage: messages.duplicateListings,
          icon: "",
        },
        {
          path: "/listings/localPagesReport",
          title: "Local Pages",
          icon: "",
        }
      ]
    },
    {
      title: "Insights",
      icon: "fas fa-analytics",
      path: "/insights",
      children: [
        // Dashboards Section
        {
          path: "/scores/summary",
          title: "LBV Score",
          helpIconMessage: messages.lbvScoreSummary,
          sectionTag: "Dashboards",
          icon: "",
        },
        {
          path: "/rankings",
          title: "SEO Rankings",
          sectionTag: "Dashboards",
          icon: "",
          children: [
            {
              path: "/rankings/avgRankings",
              helpIconMessage: messages.avgSEORankings,
              title: "SEO Rankings",
            },
            {
              path: "/rankings/page1Rankings",
              title: "Page 1 Rankings",
              hasPageTitleOverride: true,
              helpIconMessage: messages.page1SEORankings,
            },
          ],
        },
        {
          path: "/listings/searchAnalyticsReport",
          title: "Search Insights",
          sectionTag: "Dashboards",
          icon: "",
        },
        {
          path: "/reviews/storeComparison",
          title: "Location Comparison",
          sectionTag: "Dashboards",
          icon: "",
          children: [
            {
              path: "/reviews/storeComparison",
              title: "Reviews",
              hasPageTitleOverride: true
            },
            {
              path: "/social/reports",
              title: "Social",
              hasPageTitleOverride: true
            },
            {
              path: "/pulse/storeComparison",
              title: "Pulse",
              hasPageTitleOverride: true
            },
            {
              path: "/scores/storeComparison/locations",
              title: "LBV Scores By Location",
              hasPageTitleOverride: true
            },
            {
              path: "/scores/storeComparison/groups",
              title: "LBV Scores By Group",
              hasPageTitleOverride: true
            }
          ]
        },
        {
          path: "/workflow2/taskReport",
          title: "Task Reports",
          sectionTag: "Dashboards",
          icon: "",
          children: [
            {
              path: "/workflow2/taskReport",
              title: "Tasks",
              hasPageTitleOverride: true
            },
            {
              path: "/workflow2/userReport",
              title: "Users",
              hasPageTitleOverride: true
            }
          ]
        },
        {
          title: "Pulse",
          path: "/pulse/summary",
          sectionTag: "Dashboards",
          icon: "",
          children: [
            {
              path: "/pulse/summary",
              title: "Summary",
              hasPageTitleOverride: true
            },
            {
              path: "/pulse/explorer",
              title: "Explorer",
              hasPageTitleOverride: true
            }
          ]
        },
        // Analytics Studio Section
        {
          path: "/emailAlerts/manage",
          title: "Email Reports",
          sectionTag: "Analytics Studio",
          icon: ""
        },
        {
          path: "/analytics/reports",
          title: "Custom Reports",
          sectionTag: "Analytics Studio",
          icon: "",
        },
        {
          path: "/analytics/select-template",
          title: "Select Template",
          helpIconMessage: messages.selectReportTemplate,
          excludeFromMenu: true
        },
        {
          path: "/analytics/create/:templateId",
          title: "Create Report",
          hasPageTitleOverride: true,
          excludeFromMenu: true
        },
        {
          path: "/analytics/:reportId/:viewMode(edit|view)",

          hasPageTitleOverride: true,
          excludeFromMenu: true
        },
        {
          path: "/analytics/create-component",
          title: "Create Component",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/list-data-source",
          title: "Data Sources",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/create-data-source",
          title: "Create Data Source",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/edit-data-source/:dataSourceId",
          title: "Edit Data Source",
          excludeFromMenu: true,
        },
        {
          path: "/analytics/query-builder",
          title: "Query Builder",
          sectionTag: "Analytics Studio",
          icon: ""
        }
      ]
    }
  ]
};
